<script lang="ts" context="module">
  let scrollTimeout: NodeJS.Timeout;
  let load: Writable<boolean | number> = writable(false);

  export const triggerOnSeek = (value: number) => {
    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      load.set(value);
    }, 300);
  };
</script>

<script lang="ts">
  import {subtitles, project, prompt, user} from '@/store';
  import {animateScroll} from 'svelte-scrollto-element';
  import type {Sentence} from '@/types/project';
  import {createEventDispatcher, onMount, tick} from 'svelte';
  import {debounce, removePunctuation} from '@/utils/common';
  import {SentenceBlock, SearchInput, Button} from '@components';
  import {Writable, writable} from 'svelte/store';
  import {playVideo, setVideoTime} from '../video-player/VideoPlayer.svelte';
  import SkeletonLoader from '../skeleton-loader/SkeletonLoader.svelte';
  import {toTime} from '@/utils/common';
  import {tooltip} from '@/actions/tippy-action';
  //   @ts-ignore
  import VirtualList from 'svelte-virtual-list-ce';
  import {TranscriptService} from '@/services';

  let subItems: HTMLElement | null = null;
  let isReady = false;
  let action: string = '';
  let grouped: Sentence[] = [];
  let selectedSentences: Sentence[] = [];
  let loadingCount = 0;
  let allowedIndex: number = 0;
  let selectedIndex: number = 0;
  let scrolledIndex: number = 0;
  let selectedSentenceScrolledIndex: number = 0;
  let scrollTimeout: any = null;
  let searchMode: boolean = false;
  let searchWord: string = '';
  let hasSentenceSelected: boolean = false;
  let upperAllowedIndex: number = 0;
  let searchResults: Array<any> = [];
  let sentenceBatch: Array<Sentence> = [];
  let lastSelectedIndex: number | null = null;
  let initSentenceBatchUpdate: boolean = false;
  let sentenceBlocksContainer: HTMLDivElement;
  let loadingSearch = false;
  let clearedSentences: boolean = false;
  let scrollToIndex: any;
  let containerHeight: string;
  let isAdvancedSearch: boolean = false;
  let placeholder: string = '';

  export let currentTime: number = 0;
  export let doNotScroll: boolean = false;
  export let selectedHook: string = '';
  export let selectedHookIndex: number;
  const dispatch = createEventDispatcher();

  onMount(async () => {
    document.removeEventListener('keydown', saveBind);
    document.addEventListener('keydown', saveBind);

    if ($subtitles.items.grouped.length == 0) {
      await subtitles.getSubtitles($project.video?.id as number).then(() => {
        grouped = $subtitles.items.grouped;
        isReady = true;
      });
    } else {
      grouped = $subtitles.items.grouped;
      isReady = true;
    }

    prompt.set({
      ready: true
    });

    subItems = document.querySelector('#sentence-blocks');
    markSelectedSentences();
    let container = document.getElementById('list-container');
    if (container) {
      let minH = container?.clientHeight as any;
      containerHeight = minH + 'px';
    }
  });

  const markSelectedSentences = (scrollToIndex: boolean = true) => {
    if ($project.selectedSentences.length != 0) {
      hasSentenceSelected = true;

      $project.selectedSentences.forEach(sentence => {
        grouped[sentence.index].selected = true;
      });

      setTimeout(() => {
        if (scrollToIndex) {
          scrollToElement($project.selectedSentences[0].index);
        }
        dispatchTimestamp($project.selectedSentences[0].index);
      }, 1500);
    }
  };

  const setHookSelections = (hook?: any, list?: Array<number>) => {
    let indices: any;

    if ($project.selectedSentences.length > 0) {
      clearSelections();
    }

    // Get sentence indices
    if ($prompt.hooks[selectedHookIndex]?.sentenceIndices?.length) {
      indices = $prompt.hooks[selectedHookIndex].sentenceIndices;
    } else if (list?.length != 0) {
      indices = list;
    } else {
      indices = hook.indices;
    }

    clearedSentences = false;
    hasSentenceSelected = true;
    indices?.forEach((index: any, i: number) => {
      let sentence = grouped[index];

      grouped[index].selected = true;
      project.selectSentence(grouped[index]);
      selectedSentenceScrolledIndex = i;

      if (i === indices.length - 1) {
        const selectedTimestamp = sentence?.words[0]?.timeStamp?.startTime;
        dispatch('select-sentence', {
          selectedTimestamp
        });
      }
    });

    if ($project.selectedSentences.length != 0) {
      setTimeout(() => {
        let startingIndex = indices[0];
        scrollToElement(startingIndex);
        dispatchTimestamp(startingIndex);
        prompt.setStatus('completed');
      }, 1000);
    }
  };

  const saveBind = async (e: KeyboardEvent) => {
    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      saveChanges();
    }
  };

  const saveChanges = async () => {
    if ($project.changes.length) {
      await project.updateTranscription().then(async () => {
        await subtitles.getSubtitles($project.video?.id as number).then(() => {
          grouped = $subtitles.items.grouped;
          isReady = true;
        });
        markSelectedSentences(false);
      });
      sentenceChanged(true);
      project.clearChanges();
      project.set({
        compiledVideo: null,
        isNew: true,
        compiledAICameraCrewVideo: null,
        usesAICameraCrew: false,
        usesAICameraCrewV2: false,
        compiledAICameraCrewVideoV2: null,
        isUpdated: false
      });
    }
  };

  // usage for sentence selected blocks
  $: if ($project.selectedSentences.length > 0) {
    selectedSentences = [...new Set($project.selectedSentences)];
  }

  $: if (isAdvancedSearch) {
    placeholder = 'Search by meaning or context';
  } else {
    placeholder = 'Search by exact word or phrase';
  }

  $: if (selectedHook != '') {
    setHookSelections(selectedHook, []);
  }

  $: if (clearedSentences == true) {
    prompt.setActiveIndex({hook: undefined, modified: false});
  }

  // When hovered to a word, this will run
  $: onChangeAllowedIndex(allowedIndex, upperAllowedIndex);

  $: if (initSentenceBatchUpdate == true) {
    sentenceChanged(initSentenceBatchUpdate);
  }

  $: resultCount = searchResults.length;

  $: resultLabel = getResultLabel(resultCount, scrolledIndex);

  $: if ($project.selectedSentences.length <= 0) {
    lastSelectedIndex = null;
  }

  $: if ($load) {
    const sentenceOnPlay = subtitles.findSentenceOnPlay($load as number);

    if (sentenceOnPlay) {
      if (loadingCount < sentenceOnPlay.index + 1) {
        loadingCount = sentenceOnPlay.index + 1;
      }
      setTimeout(() => {
        scrollToElement(sentenceOnPlay.index);
      }, 200);
      load.set(false);
    }
  }

  const prevSentenceBlock = () => {
    selectedSentenceScrolledIndex = selectedSentenceScrolledIndex - 1;
    let index = $project.selectedSentences[selectedSentenceScrolledIndex].index;
    scrollToElement(index);
    dispatchTimestamp(index);
  };

  const nextSentenceBlock = () => {
    selectedSentenceScrolledIndex = selectedSentenceScrolledIndex + 1;
    let index = $project.selectedSentences[selectedSentenceScrolledIndex].index;
    scrollToElement(index);
    dispatchTimestamp(index);
  };

  const onChangeAllowedIndex = (allowedIndex: number, upperAllowedIndex: number) => {
    grouped = grouped.map((item, i) => {
      return {
        ...item,
        disableDrag: shouldDisableBlock(allowedIndex, upperAllowedIndex, i)
      };
    });
  };

  const scrollToElement = (id: number) => {
    scrollToIndex(id);
    const element = subItems?.querySelector(`#block-${id}`) as HTMLElement;
    animateScroll.scrollTo({
      container: '#sentence-blocks',
      element,
      duration: 500,
      offset: -12
    });
  };

  const getResultLabel = (resultCount: number, scrolledIndex: number): string => {
    if (resultCount === 0) {
      return 'No results found.';
    }

    let _result = 'result' + (resultCount === 1 ? '' : 's');

    return `${scrolledIndex + 1} of ${resultCount} ${_result} found.`;
  };

  const dispatchTimestamp = (id: number, play: boolean = false) => {
    const starttime = grouped[id].words[0].timeStamp?.startTime as number;
    setVideoTime(starttime);
    if (play) playVideo();
  };

  const searchDocument = (e: any): void => {
    searchResults = [];

    searchWord = e.target.value;

    if (searchWord === '') {
      scrolledIndex = 0;
      searchMode = false;
      scrollToElement(0);
      dispatchTimestamp(0);
      return;
    }

    grouped.forEach(sentence => {
      sentence.words.forEach(word => {
        if (removePunctuation(word.word).toLowerCase() === searchWord.toLowerCase()) {
          searchResults.push(sentence.index);
        }
      });
    });
    loadingSearch = false;
    scrolledIndex = 0;
    scrollToElement(searchResults[0]);
    dispatchTimestamp(searchResults[0]);
  };

  const handleSearch = (e: any) => {
    loadingSearch = true;
    searchMode = true;

    if (!isAdvancedSearch) {
      debounce(e, searchDocument);
    } else {
      debounce(e, searchVector);
    }
  };

  const searchVector = async (e: any) => {
    searchWord = e.target.value;

    if (searchWord === '') {
      scrolledIndex = 0;
      searchMode = false;
      scrollToElement(0);
      dispatchTimestamp(0);
      return;
    }

    await new TranscriptService()
      .searchVector($project.video?.id as number, searchWord)
      .then(response => {
        searchResults = response;
        if (searchResults.length > 0) {
          const firstResult = searchResults[0];
          scrolledIndex = 0;
          setHookSelections(null, firstResult.indices);
        }

        loadingSearch = false;
      })
      .catch(error => {
        loadingSearch = false;
      });
  };

  const disableOthers = (e: CustomEvent) => {
    action = e.detail.action;
    selectedIndex = e.detail.index;

    if (action === 'UP') {
      allowedIndex = selectedIndex - 1;
    } else if (action === 'DOWN') {
      allowedIndex = selectedIndex + 1;
    } else if (action === 'BOTH') {
      upperAllowedIndex = selectedIndex - 1;
      allowedIndex = selectedIndex + 1;
    } else if (action === '') {
      allowedIndex = 0;
      upperAllowedIndex = 0;
    }
  };

  const onFinalize = async (e: CustomEvent) => {
    let {sentence} = e.detail;
    sentenceBatch.push(sentence);
    if (sentenceBatch.length >= 1) {
      initSentenceBatchUpdate = true;

      // wait for component to update, before resetting
      await tick().then(() => {
        initSentenceBatchUpdate = false;
        sentenceBatch = [];
        action = '';
      });
    }
  };

  const shouldDisableBlock = (allowedIndex: number, upperAllowedIndex: number, index: number) => {
    let shouldDisableBlock = index !== selectedIndex && index !== allowedIndex;

    if (action === 'BOTH') {
      shouldDisableBlock = shouldDisableBlock && index !== upperAllowedIndex;
    }

    if (action === '' && initSentenceBatchUpdate) {
      return true;
    }

    return shouldDisableBlock;
  };

  const scrollUp = () => {
    scrolledIndex = scrolledIndex - 1;
    if (isAdvancedSearch) {
      setHookSelections(null, searchResults[scrolledIndex].indices);
      if (selectedHookIndex != undefined) {
        prompt.setActiveIndex({
          hook: undefined,
          modified: false
        });
      }
    } else {
      scrollToElement(searchResults[scrolledIndex]);
      dispatchTimestamp(searchResults[scrolledIndex]);
    }
  };

  const scrollDown = () => {
    scrolledIndex = scrolledIndex + 1;
    if (isAdvancedSearch) {
      setHookSelections(null, searchResults[scrolledIndex].indices);
      if (selectedHookIndex != undefined) {
        prompt.setActiveIndex({
          hook: undefined,
          modified: false
        });
      }
    } else {
      scrollToElement(searchResults[scrolledIndex]);
      dispatchTimestamp(searchResults[scrolledIndex]);
    }
  };

  const isSentenceSelected = (sentence: Sentence): boolean => {
    if ($project.selectedSentences && $project.selectedSentences.length) {
      return $project.selectedSentences.find(item => item.index == sentence.index) ? true : false;
    }

    return false;
  };

  const clearSelections = () => {
    if ($project.selectedSentences.length > 0) {
      $project.selectedSentences.forEach(sentence => {
        grouped[sentence.index].selected = false;
        project.unselectSentence(sentence);
      });
    }

    selectedSentences = [];
    hasSentenceSelected = false;
    clearedSentences = true;
    $project.changes = [];
  };

  const selectSentence = (e: CustomEvent) => {
    if (selectedHookIndex != undefined) {
      prompt.setActiveIndex({
        hook: $prompt.hooks[selectedHookIndex],
        modified: true
      });
    }

    clearedSentences = false;
    hasSentenceSelected = true;
    const {sentence, shiftKey} = e.detail;

    if (shiftKey && lastSelectedIndex !== null) {
      if (lastSelectedIndex > sentence.index) {
        for (let i = sentence.index; i <= lastSelectedIndex; i++) {
          grouped[i].selected = true;
          project.selectSentence(grouped[i]);
        }
      } else if (lastSelectedIndex < sentence.index) {
        for (let i = lastSelectedIndex + 1; i <= sentence.index; i++) {
          grouped[i].selected = true;
          project.selectSentence(grouped[i]);
        }
      }
    } else {
      project.selectSentence(sentence);
    }

    lastSelectedIndex = sentence.index;

    // set the next button for the last scrolled index of the selected sentence block
    $project.selectedSentences.map((x, i) => {
      if (lastSelectedIndex === x.index) {
        selectedSentenceScrolledIndex = i;
      }
    });

    scrollToElement(sentence.index);
    dispatchTimestamp(sentence.index);

    const selectedTimestamp = sentence.words[0].timeStamp.startTime;
    dispatch('select-sentence', {
      selectedTimestamp
    });
  };

  const unselectSentence = (e: CustomEvent) => {
    if (selectedHookIndex != undefined) {
      prompt.setActiveIndex({
        hook: $prompt.hooks[selectedHookIndex],
        modified: true
      });
    }
    const {sentence, shiftKey} = e.detail;
    const selected = grouped.filter(g => g.selected === true);

    if (selected.length > 1 && lastSelectedIndex !== null) {
      const lastIndex = selected[selected.length - 1].index;
      if (lastSelectedIndex <= lastIndex <= sentence.index) {
        lastSelectedIndex = lastIndex;
      }
    }

    if (shiftKey && lastSelectedIndex !== null) {
      if (lastSelectedIndex >= sentence.index) {
        for (let i = sentence.index; i <= lastSelectedIndex; i++) {
          grouped[i].selected = false;
          project.unselectSentence(grouped[i]);
        }
      } else if (lastSelectedIndex <= sentence.index) {
        for (let i = lastSelectedIndex; i <= sentence.index; i++) {
          grouped[i].selected = false;
          project.unselectSentence(grouped[i]);
        }
      }
    }

    if ($project.selectedSentences.length <= 1) {
      hasSentenceSelected = false;
    }

    lastSelectedIndex = sentence.index;
  };

  const sentenceChanged = (initSentenceBatchUpdate: boolean) => {
    if (selectedHookIndex != undefined) {
      prompt.setActiveIndex({
        hook: $prompt.hooks[selectedHookIndex],
        modified: true
      });
    }

    if (initSentenceBatchUpdate) {
      grouped = grouped.map((item, i) => {
        const sen: Sentence | undefined = sentenceBatch.find(sentence => sentence.index === item.index);

        project.updateSentence(sen);

        return {
          ...(sen ? sen : item),
          disableDrag: shouldDisableBlock(allowedIndex, upperAllowedIndex, i)
        };
      });
    }
  };

  const playSentence = (e: CustomEvent) => {
    dispatchTimestamp(e.detail.index, true);
  };

  const appendSentences = async (load = true, loadAll = false) => {
    grouped = grouped.map(item => {
      item.selected = isSentenceSelected(item);
      return item;
    });
  };

  const scrollSentenceBlocks = (e: Event) => {
    const target = e.target as Element;
    const {scrollHeight, scrollTop, clientHeight} = target;

    if (Math.abs(scrollHeight - scrollTop - 2) <= Math.abs(clientHeight)) {
      appendSentences();
    }
  };

  const toggleAdvancedSearch = () => {
    isAdvancedSearch = !isAdvancedSearch;
    searchResults = [];

    if (isAdvancedSearch) {
      placeholder = 'Search by meaning or context';
      if (selectedHookIndex != undefined) {
        prompt.setActiveIndex({
          hook: undefined,
          modified: false
        });
      }
    } else {
      placeholder = 'Search by exact word or phrase';
    }

    if (searchWord != '') {
      const element = document.getElementById('search-input');
      element?.dispatchEvent(new Event('input'));
    }
  };
</script>

<div
  class="relative flex h-full w-4/12 flex-shrink-0 flex-col gap-4 overflow-hidden border border-gray-200 bg-white bg-opacity-10 p-6"
>
  <div class="flex w-full flex-col gap-2">
    <div class="flex gap-2">
      <SearchInput bind:placeholder on:input={e => handleSearch(e)} />
      <Button
        icon="Up"
        iconSize="small"
        variant="primary"
        on:click={scrollUp}
        disabled={searchMode === false || scrolledIndex === 0}
      />
      <Button
        icon="Down"
        iconSize="small"
        variant="primary"
        on:click={scrollDown}
        disabled={searchMode === false || resultCount <= 0 || scrolledIndex === resultCount - 1}
      />
    </div>
    <div class="grid grid-cols-1 gap-2 p-2 md:grid-cols-2">
      <div class="text-gray-500">
        {#if searchMode && loadingSearch}
          <div class="space-x-2 font-medium italic">
            <span class="fa fa-spinner animate-spin"></span> Loading search results...
          </div>
        {:else if searchMode && !loadingSearch}
          <div class="font-medium italic">
            {resultLabel}
          </div>
        {/if}
      </div>
      <div class="flex items-center justify-end space-x-2" id="ss-smart-search">
        <span class="text-gray-500">
          <i
            use:tooltip={'Search your transcript by topic or context. This will not return exact word matches but the part of your transcript closely related to your search term.'}
            class="fa-regular fa-circle-question mr-2 text-gray-500"
          ></i>
          Smart Search
        </span>
        <label for="toggle" class="cursor-pointer font-medium text-gray-700">
          <div class="relative">
            <input
              type="checkbox"
              id="toggle"
              value={isAdvancedSearch}
              on:change={() => toggleAdvancedSearch()}
              class="peer hidden"
            />
            <div class="h-5 w-10 rounded-full bg-gray-300 transition-colors peer-checked:bg-primary"></div>
            <div
              class="absolute left-0.5 top-0.5 h-4 w-4 transform rounded-full bg-white shadow-md transition-transform peer-checked:translate-x-5"
            ></div>
          </div>
        </label>
      </div>
    </div>
  </div>
  <div class="relative h-full rounded-md border border-gray-300 bg-slate-50">
    <div
      id="sentence-blocks"
      bind:this={sentenceBlocksContainer}
      on:scroll={scrollSentenceBlocks}
      class="absolute h-full w-full overflow-y-auto !pl-4 !pt-4"
    >
      <div class="flex h-full flex-col gap-3" id="list-container">
        {#if isReady}
          <VirtualList height={containerHeight} itemHeight={120} bind:scrollToIndex items={grouped} let:item>
            <SentenceBlock
              sentence={item}
              on:on-finalize={onFinalize}
              on:play-sentence={playSentence}
              on:disable-others={disableOthers}
              on:select-sentence={selectSentence}
              on:unselect-sentence={unselectSentence}
              dropFromOthersDisabled={item.disableDrag}
              dropZone={action !== '' && !item.disableDrag}
              isFirstSentence={item.index === 0}
              isLastSentence={item.index === grouped.length - 1}
              {searchWord}
              {isAdvancedSearch}
            />
          </VirtualList>
        {:else}
          {#each Array(10) as item}
            <SkeletonLoader step="select-subtitles" {item} />
          {/each}
        {/if}
      </div>
    </div>
  </div>
  <div class="flex flex-col gap-2">
    <div class="flex space-x-4 md:inline-flex" id="clear_or_save">
      <Button
        disabled={hasSentenceSelected == false}
        on:click={clearSelections}
        className="ml-auto text-base text-center font-semibold"
        variant="negativeOutline"
        text="Clear Selections"
      />
    </div>
    <div class="relative h-full rounded-md border border-gray-300 bg-black bg-opacity-5">
      <div
        id="sentence-blocks"
        bind:this={sentenceBlocksContainer}
        on:scroll={scrollSentenceBlocks}
        class="absolute h-full w-full overflow-y-auto !p-3"
      >
        <div class="flex h-full flex-col gap-3" id="list-container">
          {#if isReady}
            <VirtualList height={containerHeight} itemHeight={120} bind:scrollToIndex items={grouped} let:item>
              <SentenceBlock
                sentence={item}
                on:on-finalize={onFinalize}
                on:play-sentence={playSentence}
                on:disable-others={disableOthers}
                on:select-sentence={selectSentence}
                on:unselect-sentence={unselectSentence}
                dropFromOthersDisabled={item.disableDrag}
                dropZone={action !== '' && !item.disableDrag}
                isFirstSentence={item.index === 0}
                isLastSentence={item.index === grouped.length - 1}
                {searchWord}
                {isAdvancedSearch}
              />
            </VirtualList>
          {:else}
            {#each Array(10) as item}
              <SkeletonLoader step="select-subtitles" {item} />
            {/each}
          {/if}
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-2">
      {#if hasSentenceSelected}
        <div class="flex items-center justify-between gap-2">
          <div class="flex w-full items-center justify-between">
            <div class="flex items-center justify-between gap-2">
              <div class="flex gap-2" id="skim_sentence">
                <Button
                  icon="Up"
                  iconSize="small"
                  variant="primary"
                  on:click={prevSentenceBlock}
                  disabled={selectedSentenceScrolledIndex === 0}
                />
                <Button
                  icon="Down"
                  iconSize="small"
                  variant="primary"
                  on:click={nextSentenceBlock}
                  disabled={$project.selectedSentences.length <= 0 ||
                    selectedSentenceScrolledIndex === $project.selectedSentences.length - 1}
                />
              </div>
              <div class="text-xs" id="total_duration">
                Selected Captions: {$project.selectedSentences.length} / {$subtitles.items.grouped.length}
              </div>
            </div>
            <div class="text-xs">
              Total duration: {toTime($project.totalDuration)}
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div>
</div>

<style lang="postcss">
  ::-webkit-scrollbar {
    @apply w-5 rounded-full bg-slate-100 p-1;
  }

  ::-webkit-scrollbar-track {
    @apply rounded-full shadow-gray-500;
  }

  ::-webkit-scrollbar-thumb {
    @apply rounded-full bg-gray-400;
  }

  ::-webkit-scrollbar-thumb:hover {
    @apply rounded-full bg-gray-500;
  }
</style>
